import { ProductsSearch } from './ProductsSearch/ProductsSearch';
import { SearchResultsBlockStyled, SearchResultsWeblineStyled, ShowResultsButtonWrapperStyled } from './Search.style';
import Heading from 'components/Basic/Heading/Heading';
import SimpleNavigation from 'components/Blocks/SimpleNavigation';
import Button from 'components/Forms/Button/Button';
import Breadcrumbs from 'components/Layout/Breadcrumbs/Breadcrumbs';
import Webline from 'components/Layout/Webline/Webline';
import { mobileFirstSizes } from 'components/Theme/mediaQueries';
import { useCurrentPageQuery } from 'helpers/queryParams/useCurrentPageQuery';
import { useCurrentSearchStringQuery } from 'helpers/queryParams/useCurrentSearchStringQuery';
import { usePersoo } from 'hooks/persoo/usePersoo';
import { useTypedTranslationFunction } from 'hooks/typescript/UseTypedTranslationFunction';
import { PERSOO_IDS } from 'modules/persoo/persooTypes';
import { useEffect, useState } from 'react';
import { BreadcrumbItemType } from 'types/breadcrumb';
import { SearchType } from 'types/search';

enum NUMBER_OF_VISIBLE_ITEMS {
    XL = 8,
    NOT_LARGE_DESKTOP = 6,
    MOBILE_XS = 4,
}

type SearchProps = {
    breadcrumbs: BreadcrumbItemType[];
    searchResults: SearchType | undefined;
};

const Search: FC<SearchProps> = ({ breadcrumbs, searchResults }) => {
    const t = useTypedTranslationFunction();
    const { isPersooAllowed, isPersooTriggered, shouldDisplayTescomaResult } = usePersoo({
        elementId: PERSOO_IDS.searchResults,
    });
    const currentPage = useCurrentPageQuery();
    const [areArticlesResultsVisible, setArticlesResultsVisibility] = useState(false);
    const [areBrandsResultsVisible, setBrandsResultsVisibility] = useState(false);
    const [areCategoriesResultsVisible, setCategoriesResultsVisibility] = useState(false);
    const [numberOfVisible, setNumberOfVisible] = useState<number>();
    const currentSearchString = useCurrentSearchStringQuery();

    useEffect(() => {
        const handler = () =>
            setNumberOfVisible(() => {
                if (window.innerWidth > mobileFirstSizes.xl) {
                    return NUMBER_OF_VISIBLE_ITEMS.XL;
                } else if (window.innerWidth > mobileFirstSizes.sm) {
                    return NUMBER_OF_VISIBLE_ITEMS.NOT_LARGE_DESKTOP;
                }

                return NUMBER_OF_VISIBLE_ITEMS.MOBILE_XS;
            });
        handler();
        window.addEventListener('resize', handler, { passive: true });

        return () => {
            window.removeEventListener('resize', handler);
        };
    }, []);

    if (searchResults === undefined) {
        return null;
    }

    return (
        <>
            <Webline>
                <Breadcrumbs breadcrumb={breadcrumbs} />
                <Heading type="h1">{`${t('Search results for')} "${currentSearchString ?? ''}"`}</Heading>
            </Webline>
            {isPersooAllowed && isPersooTriggered && <div id={PERSOO_IDS.searchResults} />}
            {currentPage === 1 && shouldDisplayTescomaResult && (
                <>
                    {searchResults.articlesSearch.length > 0 && (
                        <SearchResultsWeblineStyled>
                            <Heading type="h3">{t('Found articles')}</Heading>
                            <SearchResultsBlockStyled areAllResultsVisible={areArticlesResultsVisible}>
                                <SimpleNavigation type="article" listedItems={searchResults.articlesSearch} />
                            </SearchResultsBlockStyled>
                            {numberOfVisible !== undefined && numberOfVisible < searchResults.articlesSearch.length && (
                                <ShowResultsButtonWrapperStyled>
                                    <Button
                                        onClick={() => setArticlesResultsVisibility((prev) => !prev)}
                                        size="small"
                                        type="button"
                                    >
                                        {areArticlesResultsVisible ? t('Hide results') : t('Show all results')}
                                    </Button>
                                </ShowResultsButtonWrapperStyled>
                            )}
                        </SearchResultsWeblineStyled>
                    )}
                    {searchResults.brandSearch.length > 0 && (
                        <SearchResultsWeblineStyled>
                            <Heading type="h3">{t('Found brands')}</Heading>
                            <SearchResultsBlockStyled areAllResultsVisible={areBrandsResultsVisible}>
                                <SimpleNavigation type="brand" listedItems={searchResults.brandSearch} />
                            </SearchResultsBlockStyled>
                            {numberOfVisible !== undefined && numberOfVisible < searchResults.brandSearch.length && (
                                <ShowResultsButtonWrapperStyled>
                                    <Button
                                        onClick={() => setBrandsResultsVisibility((prev) => !prev)}
                                        size="small"
                                        type="button"
                                    >
                                        {areBrandsResultsVisible ? t('Hide results') : t('Show all results')}
                                    </Button>
                                </ShowResultsButtonWrapperStyled>
                            )}
                        </SearchResultsWeblineStyled>
                    )}
                    {searchResults.categoriesSearch.totalCount > 0 && (
                        <SearchResultsWeblineStyled>
                            <Heading type="h3">{t('Found categories')}</Heading>
                            <SearchResultsBlockStyled areAllResultsVisible={areCategoriesResultsVisible}>
                                <SimpleNavigation
                                    type="category"
                                    listedItems={searchResults.categoriesSearch.categories}
                                />
                            </SearchResultsBlockStyled>
                            {numberOfVisible !== undefined &&
                                numberOfVisible < searchResults.categoriesSearch.categories.length && (
                                    <ShowResultsButtonWrapperStyled>
                                        <Button
                                            type="button"
                                            size="small"
                                            onClick={() => setCategoriesResultsVisibility((prev) => !prev)}
                                        >
                                            {areCategoriesResultsVisible ? t('Hide results') : t('Show all results')}
                                        </Button>
                                    </ShowResultsButtonWrapperStyled>
                                )}
                        </SearchResultsWeblineStyled>
                    )}
                </>
            )}
            {shouldDisplayTescomaResult && (
                <SearchResultsWeblineStyled>
                    <Heading type="h3">{t('Found products')}</Heading>
                    <ProductsSearch productsSearch={searchResults.productsSearch} />
                </SearchResultsWeblineStyled>
            )}
        </>
    );
};

export default Search;
